import { ErrorMessage, Form, Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { SelectCategoriesModal } from '../../components/modal/app_modals/selectCategoriesModal'
import Spinner from '../../components/spinner/Spinner'
import { PageHeader } from '../../components/typography/PageHeader'
import Layout from '../../layout'
import {
  INTEREST_MESSAGE,
  INTEREST_ROUTE,
  INTEREST_STATUS,
  INTEREST_STATUS_LABEL,
} from '../../redux-toolkits/interests/interests.constant'
import {
  buildInterestFormPayload,
  filterActiveCategories,
  getInterestErrorMessage,
  getNextDisplayOrder,
} from '../../redux-toolkits/interests/interests.helpers'
import {
  useCreateInterestMutation,
  useGetAllInterestsQuery,
  useUpdateInterestMutation,
} from '../../redux-toolkits/interests/interests.slice'
import type { InterestFormValues } from '../../redux-toolkits/interests/interests.type'
import { useFetchListCategoriesQuery } from '../../redux-toolkits/lists/list.slice'
import type { ListCategoryDoc } from '../../redux-toolkits/categories/categories.type'
import { InterestFormSchema } from './validate'

const EMPTY_FORM_VALUES: InterestFormValues = {
  name: '',
  description: '',
  status: INTEREST_STATUS.DRAFT,
  categories: [],
}

/**
 * Creates or edits a non-archived Shopping Interest. Product Tags and the
 * legacy active flag are intentionally absent from all write payloads.
 */
export const InterestFormPage = () => {
  const navigate = useNavigate()
  const { interestId } = useParams<{ interestId: string }>()
  const isEditing = interestId !== undefined
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)

  const {
    data: interests = [],
    isLoading: areInterestsLoading,
    isError: hasInterestError,
  } = useGetAllInterestsQuery(null, {
    refetchOnMountOrArgChange: true,
  })
  const {
    data: categoryResponse,
    isLoading: areCategoriesLoading,
    isError: hasCategoryError,
  } = useFetchListCategoriesQuery(
    {
      skip: 0,
      limit: '1000',
      search: undefined,
      isAdmin: true,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )
  const [createInterest, { isLoading: isCreating }] =
    useCreateInterestMutation()
  const [updateInterest, { isLoading: isUpdating }] =
    useUpdateInterestMutation()

  const interest = interests.find(({ _id }) => _id === interestId)
  const activeCategories = useMemo(
    () => filterActiveCategories(categoryResponse?.data.data ?? []),
    [categoryResponse],
  )
  const activeCategoryIds = useMemo(
    () => new Set(activeCategories.map(({ _id }) => _id)),
    [activeCategories],
  )
  const activeCategoriesById = useMemo(
    () =>
      new Map(
        activeCategories.map((category) => [category._id, category] as const),
      ),
    [activeCategories],
  )

  const initialValues: InterestFormValues =
    isEditing && interest !== undefined
      ? {
          name: interest.name,
          description: interest.description,
          status:
            interest.status === INTEREST_STATUS.ACTIVE
              ? INTEREST_STATUS.ACTIVE
              : INTEREST_STATUS.DRAFT,
          categories: interest.categories
            .map(({ _id }) => activeCategoriesById.get(_id))
            .filter(
              (category): category is ListCategoryDoc => category !== undefined,
            ),
        }
      : EMPTY_FORM_VALUES

  /**
   * Trims public copy, removes inactive category mappings, and appends newly
   * created interests to the current non-archived order.
   */
  const submitForm = async (values: InterestFormValues): Promise<void> => {
    const body = buildInterestFormPayload({ values, activeCategoryIds })

    try {
      if (isEditing && interestId !== undefined) {
        await updateInterest({ interestId, body }).unwrap()
        toast.success(INTEREST_MESSAGE.UPDATED)
      } else {
        await createInterest({
          ...body,
          displayOrder: getNextDisplayOrder(interests),
        }).unwrap()
        toast.success(INTEREST_MESSAGE.CREATED)
      }
      navigate(INTEREST_ROUTE.LIST)
    } catch (error: unknown) {
      toast.error(getInterestErrorMessage(error))
    }
  }

  if (areInterestsLoading || areCategoriesLoading) return <Spinner />

  let pageErrorMessage: string = INTEREST_MESSAGE.LOAD_ERROR
  if (hasCategoryError) {
    pageErrorMessage = INTEREST_MESSAGE.CATEGORY_LOAD_ERROR
  }
  if (interest?.status === INTEREST_STATUS.ARCHIVED) {
    pageErrorMessage = INTEREST_MESSAGE.ARCHIVED_EDIT_ERROR
  }

  if (
    hasInterestError ||
    hasCategoryError ||
    (isEditing && interest === undefined) ||
    interest?.status === INTEREST_STATUS.ARCHIVED
  ) {
    return (
      <Layout>
        <div className="rounded-2xl border border-divider-gray p-8 text-center">
          <p className="text-danger">{pageErrorMessage}</p>
          <Button
            label="Back to Shopping Interests"
            size="small"
            className="!mx-auto !mt-4 !w-auto"
            onClick={() => {
              navigate(INTEREST_ROUTE.LIST)
            }}
          />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <section className="mx-auto max-w-[760px] space-y-8">
        <PageHeader
          title={
            isEditing ? 'Edit Shopping Interest' : 'Create Shopping Interest'
          }
        />

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={InterestFormSchema}
          onSubmit={submitForm}
        >
          {({
            values,
            setFieldValue,
            handleBlur,
            handleChange,
            isSubmitting,
            touched,
            errors,
          }) => (
            <Form className="space-y-6 rounded-2xl border border-divider-gray bg-white p-6">
              <div>
                <label htmlFor="name" className="mb-2 block font-medium">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-describedby="name-error"
                  aria-invalid={
                    touched.name === true && errors.name !== undefined
                  }
                  placeholder="e.g. Everyday Living"
                  className="w-full rounded-md border border-divider-gray p-4 outline-none focus:border-black"
                />
                <p
                  id="name-error"
                  role="alert"
                  className="mt-1 text-sm text-danger"
                >
                  <ErrorMessage name="name" />
                </p>
              </div>

              <div>
                <label htmlFor="description" className="mb-2 block font-medium">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-describedby="description-error"
                  aria-invalid={
                    touched.description === true &&
                    errors.description !== undefined
                  }
                  placeholder="Describe what this interest helps customers find."
                  className="w-full rounded-md border border-divider-gray p-4 outline-none focus:border-black"
                />
                <p
                  id="description-error"
                  role="alert"
                  className="mt-1 text-sm text-danger"
                >
                  <ErrorMessage name="description" />
                </p>
              </div>

              <div>
                <label htmlFor="status" className="mb-2 block font-medium">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full rounded-md border border-divider-gray bg-white p-4 outline-none focus:border-black"
                >
                  <option value={INTEREST_STATUS.DRAFT}>
                    {INTEREST_STATUS_LABEL[INTEREST_STATUS.DRAFT]}
                  </option>
                  <option value={INTEREST_STATUS.ACTIVE}>
                    {INTEREST_STATUS_LABEL[INTEREST_STATUS.ACTIVE]}
                  </option>
                </select>
              </div>

              <div className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">Product Categories</p>
                    <p className="text-sm text-pry-gray">
                      Only active, top-level categories can be mapped.
                    </p>
                  </div>
                  <Button
                    type="button"
                    label="Select Categories"
                    size="small"
                    onClick={() => {
                      setCategoryModalOpen(true)
                    }}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  {values.categories.length > 0 ? (
                    values.categories.map((category) => (
                      <span
                        key={category._id}
                        className="rounded-full bg-bg-gray px-4 py-2 text-sm"
                      >
                        {category.name}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-pry-gray">
                      No categories selected.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-divider-gray pt-6 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  label="Cancel"
                  size="medium"
                  onClick={() => {
                    navigate(INTEREST_ROUTE.LIST)
                  }}
                />
                <Button
                  type="submit"
                  label="Save Interest"
                  size="medium"
                  primary
                  loading={isCreating || isUpdating}
                  disabled={
                    isSubmitting || isCreating || isUpdating || hasCategoryError
                  }
                />
              </div>

              <SelectCategoriesModal
                open={categoryModalOpen}
                setOpen={setCategoryModalOpen}
                selectedCategories={values.categories.filter((category) =>
                  activeCategoryIds.has(category._id),
                )}
                onSuccessCallback={(selectedCategories) => {
                  void setFieldValue('categories', selectedCategories)
                  setCategoryModalOpen(false)
                }}
              />
            </Form>
          )}
        </Formik>
      </section>
    </Layout>
  )
}
